/**
 * The input half of the GLSL bootstrap: binds every contract uniform on its
 * texture unit and keeps the textures current — the image, the playing video,
 * the audio analysis, the text and the 3D model rendered as a turntable.
 *
 * Uniform names, units and sizes are interpolated from `contract.ts`, never
 * restated. The returned text is spliced into the bootstrap's IIFE after the
 * program is linked and relies on its scope: `gl`, `canvas`, `program`,
 * `buffer` (the fullscreen quad), `positionLoc` and `fail`. It defines
 * `RESERVED` (uniforms never bound as params), `updateInputs(seconds)` (run
 * before each draw) and `applyInputUniforms()` (run after `useProgram`).
 */

import {
  AUDIO_SILENCE_DB,
  GLSL_AUDIO_TEXTURE,
  GLSL_BUILTIN_UNIFORMS,
  GLSL_INPUT_UNIFORMS,
  GLSL_MODEL_TEXTURE,
  GLSL_RESERVED_UNIFORMS,
  GLSL_TEXT_TEXTURE,
} from './contract'

/** Distance from the camera to the model's unit bounding sphere. */
const CAMERA_DISTANCE = 3

const MODEL_VERTEX_SHADER = [
  'attribute vec3 a_position;',
  'attribute vec3 a_normal;',
  'uniform mat4 u_modelView;',
  'uniform mat4 u_projection;',
  'varying vec3 v_normal;',
  'varying vec3 v_position;',
  'void main() {',
  '  vec4 p = u_modelView * vec4(a_position, 1.0);',
  '  v_position = p.xyz;',
  '  v_normal = (u_modelView * vec4(a_normal, 0.0)).xyz;',
  '  gl_Position = u_projection * p;',
  '}',
].join('\n')

// rgb: view-space normal packed to 0..1. a: 1 nearest, toward 0 at the back, never 0 on the mesh.
const MODEL_FRAGMENT_SHADER = [
  'precision mediump float;',
  'uniform vec2 u_depthRange;',
  'varying vec3 v_normal;',
  'varying vec3 v_position;',
  'void main() {',
  '  vec3 n = normalize(v_normal);',
  '  if (dot(n, -v_position) < 0.0) n = -n;',
  '  float d = clamp((-v_position.z - u_depthRange.x) / (u_depthRange.y - u_depthRange.x), 0.0, 1.0);',
  '  gl_FragColor = vec4(n * 0.5 + 0.5, mix(1.0, 0.04, d));',
  '}',
].join('\n')

export function buildGlslInputsCode(): string {
  return `
  // --- Inputs (names, units and sizes from media/contract.ts) ---
  var INPUT_UNIFORMS = ${JSON.stringify(GLSL_INPUT_UNIFORMS)};
  var BUILTIN_UNIFORMS = ${JSON.stringify(GLSL_BUILTIN_UNIFORMS)};
  var AUDIO_TEXTURE = ${JSON.stringify(GLSL_AUDIO_TEXTURE)};
  var SILENCE_DB = ${AUDIO_SILENCE_DB};
  var TURN_SECONDS = ${GLSL_MODEL_TEXTURE.turntableSecondsPerTurn};
  var CAMERA_DISTANCE = ${CAMERA_DISTANCE};
  var RESERVED = {};
  ${JSON.stringify(GLSL_RESERVED_UNIFORMS)}.forEach(function(name) { RESERVED[name] = 1; });
  var media = window.__genieMedia || { audioConnected: false };

  function uniform(name) { return name ? gl.getUniformLocation(program, name) : null; }
  var uTime = uniform(BUILTIN_UNIFORMS.time);
  var uResolution = uniform(BUILTIN_UNIFORMS.resolution);
  var uMouse = uniform(BUILTIN_UNIFORMS.mouse);
  var uAmplitude = uniform(BUILTIN_UNIFORMS.amplitude);

  function bindSlot(slot) {
    gl.activeTexture(gl.TEXTURE0 + slot.unit);
    gl.bindTexture(gl.TEXTURE_2D, slot.texture);
  }
  // WebGL1 samples non-power-of-two textures only with clamping and no mipmaps.
  function clearTexture(slot) {
    bindSlot(slot);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array(4));
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    slot.loaded = false;
    slot.width = 0;
    slot.height = 0;
    slot.sized = false;
  }
  function uploadSource(slot, source) {
    bindSlot(slot);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
  }

  var slots = {}, slotList = [];
  Object.keys(INPUT_UNIFORMS).forEach(function(type) {
    var u = INPUT_UNIFORMS[type];
    var slot = { unit: u.unit, sampler: uniform(u.sampler), has: uniform(u.has), resolution: uniform(u.resolution), texture: gl.createTexture() };
    clearTexture(slot);
    slots[type] = slot;
    slotList.push(slot);
  });

  // --- Image ---
  var imageToken = 0;
  function loadImage(data) {
    var token = ++imageToken, slot = slots.image;
    if (typeof data !== 'string' || !data) { clearTexture(slot); return; }
    var img = new Image();
    img.onload = function() {
      if (token !== imageToken) return;
      uploadSource(slot, img);
      slot.loaded = true;
      slot.width = img.naturalWidth;
      slot.height = img.naturalHeight;
    };
    img.onerror = function() { if (token === imageToken) fail('input', 'The input image could not be decoded'); };
    img.src = data;
  }
  window.genie.onInput('image', loadImage);
  if (window.genie.inputImage) loadImage(window.genie.inputImage);

  // --- Video: a new texture whenever the element shows a new frame ---
  var videoFrom = null, videoTime = -1, videoFailed = false;
  function updateVideo() {
    var slot = slots.video, el = window.genie.inputVideo;
    if (!el) {
      if (slot.loaded || videoFrom) clearTexture(slot);
      videoFrom = null;
      return;
    }
    if (el.readyState < 2 || !el.videoWidth) return;
    slot.width = el.videoWidth;
    slot.height = el.videoHeight;
    if (!slot.sampler) { slot.loaded = true; return; }
    if (el === videoFrom && el.currentTime === videoTime) return;
    try {
      uploadSource(slot, el);
      slot.loaded = true;
      videoFrom = el;
      videoTime = el.currentTime;
    } catch (e) {
      if (!videoFailed) { videoFailed = true; fail('input', 'The video input could not be drawn: ' + (e && e.message)); }
    }
  }

  // --- Audio: row 0 FFT magnitude, row 1 waveform (0.5 is silence), bytes in every channel ---
  var audioPixels = new Uint8Array(AUDIO_TEXTURE.width * AUDIO_TEXTURE.height * 4);
  var audioDirty = true;
  window.genie.onInput('audio', function() { audioDirty = true; });
  function packAudio() {
    var audio = window.genie.audio, w = AUDIO_TEXTURE.width;
    for (var i = 0; i < w; i++) {
      var level = (audio.fft[i] - SILENCE_DB) / -SILENCE_DB;
      level = level > 0 ? (level < 1 ? level : 1) : 0;
      var wave = audio.waveform[i] * 0.5 + 0.5;
      wave = wave >= 0 ? (wave < 1 ? wave : 1) : wave < 0 ? 0 : 0.5;
      var f = Math.round(level * 255), s = Math.round(wave * 255), o = i * 4, p = (w + i) * 4;
      audioPixels[o] = audioPixels[o + 1] = audioPixels[o + 2] = audioPixels[o + 3] = f;
      audioPixels[p] = audioPixels[p + 1] = audioPixels[p + 2] = audioPixels[p + 3] = s;
    }
  }
  function updateAudio() {
    var slot = slots.audio;
    if (!slot.sampler || !audioDirty) { slot.loaded = !!media.audioConnected; return; }
    audioDirty = false;
    packAudio();
    bindSlot(slot);
    if (!slot.sized) {
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, AUDIO_TEXTURE.width, AUDIO_TEXTURE.height, 0, gl.RGBA, gl.UNSIGNED_BYTE, audioPixels);
      slot.sized = true;
    } else {
      gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, AUDIO_TEXTURE.width, AUDIO_TEXTURE.height, gl.RGBA, gl.UNSIGNED_BYTE, audioPixels);
    }
    slot.loaded = !!media.audioConnected;
  }

  // --- Text: white on transparent, centred, wrapped and sized to fill the canvas ---
  var textCanvas = null, textKey = '';
  function wrapLines(ctx, text, maxWidth) {
    var lines = [], paragraphs = text.split('\\n');
    for (var p = 0; p < paragraphs.length; p++) {
      var words = paragraphs[p].split(' '), line = '';
      for (var w = 0; w < words.length; w++) {
        var next = line ? line + ' ' + words[w] : words[w];
        if (!line || ctx.measureText(next).width <= maxWidth) line = next;
        else { lines.push(line); line = words[w]; }
      }
      lines.push(line);
    }
    return lines;
  }
  function drawText(target, text) {
    var ctx = target.getContext('2d'), width = target.width, height = target.height;
    var maxWidth = width * ${GLSL_TEXT_TEXTURE.fill}, maxHeight = height * ${GLSL_TEXT_TEXTURE.fill};
    var lo = 4, hi = Math.max(4, Math.floor(maxHeight)), size = 4, lines = null;
    while (lo <= hi) {
      var mid = (lo + hi) >> 1;
      ctx.font = 'bold ' + mid + 'px sans-serif';
      var candidate = wrapLines(ctx, text, maxWidth), widest = 0;
      for (var i = 0; i < candidate.length; i++) widest = Math.max(widest, ctx.measureText(candidate[i]).width);
      if (widest <= maxWidth && candidate.length * mid * 1.2 <= maxHeight) { size = mid; lines = candidate; lo = mid + 1; }
      else hi = mid - 1;
    }
    ctx.font = 'bold ' + size + 'px sans-serif';
    if (!lines) lines = wrapLines(ctx, text, maxWidth);
    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = ${JSON.stringify(GLSL_TEXT_TEXTURE.color)};
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    var lineHeight = size * 1.2, top = height / 2 - (lines.length - 1) * lineHeight / 2;
    for (var l = 0; l < lines.length; l++) ctx.fillText(lines[l], width / 2, top + l * lineHeight);
  }
  function updateText() {
    var slot = slots.text, text = window.genie.inputText;
    var has = typeof text === 'string' && text.length > 0;
    if (!slot.sampler) { slot.loaded = has; return; }
    var key = has ? canvas.width + 'x' + canvas.height + '|' + text : '';
    if (key === textKey) return;
    textKey = key;
    if (!has) { clearTexture(slot); return; }
    if (!textCanvas) textCanvas = document.createElement('canvas');
    textCanvas.width = canvas.width;
    textCanvas.height = canvas.height;
    drawText(textCanvas, text.slice(0, ${GLSL_TEXT_TEXTURE.maxLength}));
    uploadSource(slot, textCanvas);
    slot.loaded = true;
  }

  // --- 3D: a turntable render into an offscreen framebuffer (normal in rgb, depth in a) ---
  var MODEL_VS = ${JSON.stringify(MODEL_VERTEX_SHADER)};
  var MODEL_FS = ${JSON.stringify(MODEL_FRAGMENT_SHADER)};
  var model = { program: null, meshes: [], fbo: null, depth: null, width: 0, height: 0, center: [0, 0, 0], scale: 1, broken: false };
  var uintIndices = gl.getExtension('OES_element_index_uint');

  function compileModelProgram() {
    var vs = gl.createShader(gl.VERTEX_SHADER), fs = gl.createShader(gl.FRAGMENT_SHADER), p = gl.createProgram();
    gl.shaderSource(vs, MODEL_VS); gl.compileShader(vs);
    gl.shaderSource(fs, MODEL_FS); gl.compileShader(fs);
    gl.attachShader(p, vs); gl.attachShader(p, fs); gl.linkProgram(p);
    if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
      model.broken = true;
      fail('runtime', 'The 3D input renderer failed to start: ' + (gl.getProgramInfoLog(p) || gl.getShaderInfoLog(vs) || gl.getShaderInfoLog(fs)));
      return false;
    }
    model.program = p;
    model.aPosition = gl.getAttribLocation(p, 'a_position');
    model.aNormal = gl.getAttribLocation(p, 'a_normal');
    model.uModelView = gl.getUniformLocation(p, 'u_modelView');
    model.uProjection = gl.getUniformLocation(p, 'u_projection');
    model.uDepthRange = gl.getUniformLocation(p, 'u_depthRange');
    return true;
  }

  function computeNormals(positions, indices) {
    var normals = new Float32Array(positions.length);
    var count = indices ? indices.length : positions.length / 3;
    for (var t = 0; t + 2 < count; t += 3) {
      var a = (indices ? indices[t] : t) * 3, b = (indices ? indices[t + 1] : t + 1) * 3, c = (indices ? indices[t + 2] : t + 2) * 3;
      var ux = positions[b] - positions[a], uy = positions[b + 1] - positions[a + 1], uz = positions[b + 2] - positions[a + 2];
      var vx = positions[c] - positions[a], vy = positions[c + 1] - positions[a + 1], vz = positions[c + 2] - positions[a + 2];
      var nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx;
      normals[a] += nx; normals[a + 1] += ny; normals[a + 2] += nz;
      normals[b] += nx; normals[b + 1] += ny; normals[b + 2] += nz;
      normals[c] += nx; normals[c + 1] += ny; normals[c + 2] += nz;
    }
    for (var i = 0; i < normals.length; i += 3) {
      var length = Math.sqrt(normals[i] * normals[i] + normals[i + 1] * normals[i + 1] + normals[i + 2] * normals[i + 2]) || 1;
      normals[i] /= length; normals[i + 1] /= length; normals[i + 2] /= length;
    }
    return normals;
  }

  function arrayBuffer(data) {
    var b = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, b);
    gl.bufferData(gl.ARRAY_BUFFER, data, gl.STATIC_DRAW);
    return b;
  }

  function prepareModel(data) {
    for (var r = 0; r < model.meshes.length; r++) {
      gl.deleteBuffer(model.meshes[r].position);
      gl.deleteBuffer(model.meshes[r].normal);
      if (model.meshes[r].index) gl.deleteBuffer(model.meshes[r].index);
    }
    model.meshes = [];
    if (!data) { clearTexture(slots['3d']); model.width = 0; model.height = 0; return; }
    var min = data.bounds.min, max = data.bounds.max;
    var dx = max[0] - min[0], dy = max[1] - min[1], dz = max[2] - min[2];
    model.center = [(min[0] + max[0]) / 2, (min[1] + max[1]) / 2, (min[2] + max[2]) / 2];
    model.scale = 1 / (Math.sqrt(dx * dx + dy * dy + dz * dz) / 2 || 1);
    for (var m = 0; m < data.meshes.length; m++) {
      var mesh = data.meshes[m], positions = mesh.positions;
      if (!positions || positions.length < 9) continue;
      var indices = mesh.indices && mesh.indices.length ? mesh.indices : null;
      var normals = mesh.normals && mesh.normals.length === positions.length ? mesh.normals : computeNormals(positions, indices);
      if (indices && !uintIndices) {
        // No 32-bit indices on this GPU: draw the triangles unindexed instead.
        var flatPositions = new Float32Array(indices.length * 3), flatNormals = new Float32Array(indices.length * 3);
        for (var k = 0; k < indices.length; k++) {
          for (var c = 0; c < 3; c++) {
            flatPositions[k * 3 + c] = positions[indices[k] * 3 + c];
            flatNormals[k * 3 + c] = normals[indices[k] * 3 + c];
          }
        }
        positions = flatPositions; normals = flatNormals; indices = null;
      }
      var entry = { position: arrayBuffer(positions), normal: arrayBuffer(normals), index: null, count: indices ? indices.length : positions.length / 3 };
      if (indices) {
        entry.index = gl.createBuffer();
        gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, entry.index);
        gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, indices instanceof Uint32Array ? indices : new Uint32Array(indices), gl.STATIC_DRAW);
      }
      model.meshes.push(entry);
    }
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
  }
  window.genie.onInput('3d', function(data) { if (slots['3d'].sampler) prepareModel(data); });
  if (slots['3d'].sampler && window.genie.inputModel) prepareModel(window.genie.inputModel);

  function ensureModelTarget() {
    var slot = slots['3d'], w = canvas.width, h = canvas.height;
    if (model.fbo && model.width === w && model.height === h) return true;
    bindSlot(slot);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, w, h, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
    if (!model.fbo) { model.fbo = gl.createFramebuffer(); model.depth = gl.createRenderbuffer(); }
    gl.bindRenderbuffer(gl.RENDERBUFFER, model.depth);
    gl.renderbufferStorage(gl.RENDERBUFFER, gl.DEPTH_COMPONENT16, w, h);
    gl.bindFramebuffer(gl.FRAMEBUFFER, model.fbo);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, slot.texture, 0);
    gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.DEPTH_ATTACHMENT, gl.RENDERBUFFER, model.depth);
    var complete = gl.checkFramebufferStatus(gl.FRAMEBUFFER) === gl.FRAMEBUFFER_COMPLETE;
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    if (!complete) { model.broken = true; fail('runtime', 'The 3D input cannot be rendered on this device'); return false; }
    model.width = w;
    model.height = h;
    return true;
  }

  function multiply(a, b) {
    var out = new Array(16);
    for (var col = 0; col < 4; col++) {
      for (var row = 0; row < 4; row++) {
        var sum = 0;
        for (var k = 0; k < 4; k++) sum += a[k * 4 + row] * b[col * 4 + k];
        out[col * 4 + row] = sum;
      }
    }
    return out;
  }
  function translation(x, y, z) { return [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, x, y, z, 1]; }
  function rotationX(a) { var c = Math.cos(a), s = Math.sin(a); return [1, 0, 0, 0, 0, c, s, 0, 0, -s, c, 0, 0, 0, 0, 1]; }
  function rotationY(a) { var c = Math.cos(a), s = Math.sin(a); return [c, 0, -s, 0, 0, 1, 0, 0, s, 0, c, 0, 0, 0, 0, 1]; }

  function renderModel(seconds) {
    var slot = slots['3d'];
    if (!slot.sampler || model.broken || !model.meshes.length) return;
    if (!model.program && !compileModelProgram()) return;
    if (!ensureModelTarget()) return;
    var s = model.scale, c = model.center;
    var fit = [s, 0, 0, 0, 0, s, 0, 0, 0, 0, s, 0, -c[0] * s, -c[1] * s, -c[2] * s, 1];
    var turn = multiply(rotationX(0.35), multiply(rotationY(seconds / TURN_SECONDS * Math.PI * 2), fit));
    var modelView = multiply(translation(0, 0, -CAMERA_DISTANCE), turn);
    // Fit the unit sphere to the shorter side, with a little margin.
    var f = 1 / Math.tan(Math.asin(1 / CAMERA_DISTANCE) * 1.1), aspect = model.width / model.height;
    var fx = aspect >= 1 ? f / aspect : f, fy = aspect >= 1 ? f : f * aspect, near = 0.1, far = 20;
    var projection = [fx, 0, 0, 0, 0, fy, 0, 0, 0, 0, (far + near) / (near - far), -1, 0, 0, 2 * far * near / (near - far), 0];

    gl.bindFramebuffer(gl.FRAMEBUFFER, model.fbo);
    gl.viewport(0, 0, model.width, model.height);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
    gl.enable(gl.DEPTH_TEST);
    gl.useProgram(model.program);
    gl.uniformMatrix4fv(model.uModelView, false, new Float32Array(modelView));
    gl.uniformMatrix4fv(model.uProjection, false, new Float32Array(projection));
    gl.uniform2f(model.uDepthRange, CAMERA_DISTANCE - 1, CAMERA_DISTANCE + 1);
    gl.enableVertexAttribArray(model.aPosition);
    gl.enableVertexAttribArray(model.aNormal);
    for (var i = 0; i < model.meshes.length; i++) {
      var mesh = model.meshes[i];
      gl.bindBuffer(gl.ARRAY_BUFFER, mesh.position);
      gl.vertexAttribPointer(model.aPosition, 3, gl.FLOAT, false, 0, 0);
      gl.bindBuffer(gl.ARRAY_BUFFER, mesh.normal);
      gl.vertexAttribPointer(model.aNormal, 3, gl.FLOAT, false, 0, 0);
      if (mesh.index) {
        gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, mesh.index);
        gl.drawElements(gl.TRIANGLES, mesh.count, gl.UNSIGNED_INT, 0);
      } else {
        gl.drawArrays(gl.TRIANGLES, 0, mesh.count);
      }
    }
    gl.disableVertexAttribArray(model.aPosition);
    gl.disableVertexAttribArray(model.aNormal);
    gl.disable(gl.DEPTH_TEST);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);

    // Back to the user's program and the fullscreen quad (WebGL1 has no vertex array objects).
    gl.useProgram(program);
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.enableVertexAttribArray(positionLoc);
    gl.vertexAttribPointer(positionLoc, 2, gl.FLOAT, false, 0, 0);
    slot.loaded = true;
  }

  function updateInputs(seconds) {
    updateVideo();
    updateAudio();
    updateText();
    if (slots['3d'].sampler) renderModel(seconds);
    else slots['3d'].loaded = !!window.genie.inputModel;
  }

  function applyInputUniforms() {
    for (var i = 0; i < slotList.length; i++) {
      var slot = slotList[i];
      if (slot.sampler) gl.uniform1i(slot.sampler, slot.unit);
      if (slot.has) gl.uniform1f(slot.has, slot.loaded ? 1 : 0);
      if (slot.resolution) gl.uniform2f(slot.resolution, slot.width, slot.height);
    }
    if (uAmplitude) gl.uniform1f(uAmplitude, media.audioConnected ? window.genie.audio.amplitude : 0);
  }
`
}
